import { getPmsProviderSupportsDocumentImport } from '@/lib/services/integrations/pms/provider-presentation';

import type { Matter } from './types';

/**
 * Whether a matter's recorded sync `source` names the PMS the firm has **now**.
 *
 * `metadata.pms.source` records which PMS a matter was synced to or imported
 * from; `matter.pmsProviderName` is the firm's *currently active* integration
 * (the backend sets it from `findActivePms`, so it is absent when the firm has
 * no enabled PMS). They agree for every matter until the firm switches
 * provider — after which every previously-synced matter still carries the
 * **retired** provider's name.
 *
 * **Ask this, never `!!source`.** A presence-only check reads a stranded matter
 * as "synced" and suppresses the very affordances that would let the lawyer
 * sync it to the new provider, while `pmsSynced` independently reports false
 * (it resolves the mapping against the active integration, and the mapping sits
 * under the retired one). The matter ends up simultaneously synced enough to
 * hide every control and unsynced enough to be absent from the new PMS.
 *
 * Case-insensitive because the two fields are typed independently and provider
 * names are stored lower-case but arrive from several writers.
 */
export function isSyncedToCurrentPms(matter: Matter | undefined): boolean {
  return isPmsSourceCurrent(
    matter?.metadata?.pms?.source,
    matter?.pmsProviderName
  );
}

/**
 * {@link isSyncedToCurrentPms} for a caller that holds the two values rather
 * than the matter — the sidebar header, which takes them as props.
 *
 * Both are absent-able and typed independently (`source` is a `PmsProvider`,
 * `currentProvider` also admits the unimplemented `leap`/`clio`), so this
 * compares case-insensitively and treats either being missing as "not synced":
 * a firm with no enabled PMS has nothing for a matter to be current *with*.
 */
export function isPmsSourceCurrent(
  source: string | undefined,
  currentProvider: string | undefined
): boolean {
  if (!source || !currentProvider) return false;

  return source.toLowerCase() === currentProvider.toLowerCase();
}

/**
 * Whether this matter's PMS can serve an inbound document import, and so whether
 * a surface may offer to pull documents from it.
 *
 * Gate every "import / attach from {PMS}" affordance on this rather than on the
 * mere presence of `metadata.pms.source`: a presence-only check advertises the
 * affordance on a matter whose provider cannot fulfil it, which can only end in
 * an error once the user commits to the flow.
 *
 * That includes a matter stranded on a **retired** provider — the firm can no
 * longer reach it, so offering to import from it fails at the endpoint.
 *
 * Which providers have the legs at all is **not** decided here: that is the
 * `supportsDocumentImport` field of the provider-presentation registry, read via
 * `getPmsProviderSupportsDocumentImport`. This module used to keep a second list
 * (`PMS_PROVIDERS_WITH_DOCUMENT_IMPORT`) and the two drifted — ActionStep was in
 * the list here but `false` in the registry, so the Extract wizard's sync button
 * worked while the PMS review banner never rendered at all (VALE-356).
 */
export function canImportDocumentsFromPms(matter: Matter | undefined): boolean {
  return canImportDocumentsFromPmsSource(
    matter?.metadata?.pms?.source,
    matter?.pmsProviderName
  );
}

/**
 * {@link canImportDocumentsFromPms} for a caller that holds the two values rather
 * than the matter — the matter layout, which reads them off its PMS metadata and
 * header data. Same pairing as {@link isSyncedToCurrentPms} / {@link isPmsSourceCurrent}.
 *
 * **The matter layout's PMS review banner gates on this**, so the banner and every
 * other import affordance resolve to one predicate over one capability declaration.
 * The banner used to re-assemble the pair inline, which is how it kept its own copy
 * of a condition that had already drifted (VALE-356).
 */
export function canImportDocumentsFromPmsSource(
  source: string | undefined,
  currentProvider: string | undefined
): boolean {
  return (
    isPmsSourceCurrent(source, currentProvider) &&
    getPmsProviderSupportsDocumentImport(source)
  );
}

/**
 * The providers that perform an **outward matter sync** — pushing a Vale matter into the PMS once its
 * config panel is saved.
 *
 * Smokeball and ActionStep both implement matter creation (`createMatter` on each provider);
 * Clio and LEAP have no provider implementation at all, so nothing will ever complete for them.
 *
 * **Add a provider here when its outward matter-creation leg lands** — that is the whole change
 * needed for its sync progress to surface in the UI.
 */
export const PMS_PROVIDERS_WITH_OUTWARD_MATTER_SYNC: readonly string[] = [
  'smokeball',
  'actionstep'
];

/**
 * How often the matter layout polls the matter-detail query while an outward PMS
 * sync is pending. Matches the AI-analysis poll cadence
 * (`use-matter-ai-analysis-polling.ts`). React Query pauses the interval while
 * the tab is unfocused, so this never runs in the background.
 */
export const PMS_SYNC_POLL_INTERVAL_MS = 5_000;

/**
 * True while an outward PMS sync is *expected but not yet complete* for this
 * matter.
 *
 * Once a lawyer completes the provider's config panel, Vale fires a
 * fire-and-forget outward sync (`MATTER_UPDATED` → the PMS matter-sync
 * listener). When it succeeds the backend sets `metadata.pms.source` and
 * creates the matter entity mapping (which drives `pmsSynced`), and the sidebar
 * header's "Synced with {provider}" badge appears. Nothing pushes that
 * completion to the browser, so the matter layout polls the matter-detail query
 * while this predicate holds and the status auto-displays without a manual
 * refresh — stopping the moment the matter is synced or the lawyer leaves.
 *
 * Conditions (all must hold):
 * - the firm's PMS performs outward matter sync (see
 *   {@link PMS_PROVIDERS_WITH_OUTWARD_MATTER_SYNC} — `pmsProviderName` is only
 *   set when an enabled PMS integration exists). This was pinned to
 *   `'smokeball'` until LF-6344, so an ActionStep firm saved the panel
 *   [ASTEP-16] shipped and then saw nothing until a manual reload;
 * - the matter is not yet synced (`!pmsSynced`, and not already synced to the
 *   firm's current PMS — a `source` naming a *retired* provider does not count);
 * - the outward sync config is complete — matter type + state of matter are
 *   persisted on `metadata.pms` (exactly what the backend requires to create
 *   the matter in the PMS, and what both providers' panels persist).
 */
export function isPmsSyncPending(matter: Matter | undefined): boolean {
  if (!matter) return false;
  if (matter.pmsSynced) return false;
  if (
    !matter.pmsProviderName ||
    !PMS_PROVIDERS_WITH_OUTWARD_MATTER_SYNC.includes(matter.pmsProviderName)
  ) {
    return false;
  }

  const pms = matter.metadata?.pms;
  if (!pms) return false;
  // Never poll a matter already synced to the firm's CURRENT PMS. A `source`
  // naming a *retired* provider is not that: such a matter is stranded and can
  // legitimately be re-synced, so it stays pollable (see isSyncedToCurrentPms).
  if (isSyncedToCurrentPms(matter)) return false;

  return !!pms.matterTypeId && !!pms.stateOfMatter;
}
